import { ButtonBase, createStyles, makeStyles } from "@material-ui/core";
import {
    baseColors,
    Breakpoint,
    DotLoader,
    getUserFromStorage,
    PieChart,
    PiePieceData,
    RelodeTheme,
    Switch,
    Text,
    useDeviceSize,
} from "@relode/core";
import { JobCandidateStatus, JobStatus, UserRole } from "@relode/core/domain";
import classnames from "classnames";
import * as React from "react";

import {
    DashboardContent,
    DashboardTile,
    DashboardTileSection,
} from "../../../shared/components";
import { useDashboardContext } from "../_infra/dashboardProvider";
import {
    goToCandidateSearchStatus,
    goToJobSearch,
    goToJobSearchStatus,
} from "../goToSearch";
import { QuestionsSection } from "../questionsSection";
import { DashboardStatTile } from "./dashboardStatTile";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            chartJobTitle: {
                fontSize: 20,
                color: baseColors.gray,
            },
            chartCenterContainer: {
                textAlign: "center",
            },
            chartContainer: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "275px",
                flexBasis: "40%",
                [theme.breakpoints.down("sm")]: {
                    gridRowStart: "unset",
                    gridRowEnd: "unset",
                    flexDirection: "column",
                },
            },
            sectionTitleFlex: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            },
            statisticsContainer: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                [theme.breakpoints.down("sm")]: {
                    alignItems: "unset",
                    flexDirection: "column",
                    marginTop: theme.spacing(2),
                },
            },
            statsContainer: {
                display: "flex",
                flexDirection: "column",
                flexBasis: "60%",
                borderLeft: "1px solid lightgray",
                [theme.breakpoints.down("sm")]: {
                    borderLeft: "none",
                },
            },
            statsContainer2: {
                display: "flex",
                flexDirection: "column",
                padding: theme.spacing(3, 6, 4, 6),
                [theme.breakpoints.down("sm")]: {
                    alignItems: "center",
                    borderTop: "1px solid #DDDDDF !important", // need important to override styling put in for other areas.
                },
            },
            statsContainer3: {
                display: "grid",
                gridRowGap: theme.spacing(2),
                gridColumnGap: theme.spacing(2),
                gridTemplateColumns: "repeat(auto-fit, minmax(184px, 1fr))",
                [theme.breakpoints.down("sm")]: {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                },
            },
            topBorder: {
                borderTop: "1px solid #DDDDDF",
            },
            successContainer: {
                gridColumnStart: 2,
                padding: theme.spacing(0, 6),
                display: "flex",
                flexDirection: "column",
                [theme.breakpoints.down("sm")]: {
                    gridColumnStart: "unset",
                },
            },
            statisticsTileContainer: {
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                justifyContent: "space-between",
                [theme.breakpoints.down("sm")]: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                },
            },
            qualityContainer: {
                borderTop: "1px solid lightgray",
                gridColumnStart: 2,
                padding: theme.spacing(0, 6),
                display: "flex",
                flexDirection: "column",
                [theme.breakpoints.down("sm")]: {
                    gridColumnStart: "unset",
                },
            },
            statSectionTitle: {
                fontSize: 20,
                marginBottom: theme.spacing(2),
                [theme.breakpoints.down("sm")]: {
                    marginBottom: theme.spacing(4),
                },
            },
            statsSyncDate: {
                color: baseColors.gray,
            },
            totalJobsButton: {
                display: "flex",
                flexDirection: "column",
            },
        }),
    { name: "HiringTeamManagerDashboard" }
);

export const HiringTeamManagerDashboard = () => {
    const {
        state: {
            data: {
                managerStats,
                jobManagerDetails,
                showAllJobs,
                managerJobCounts,
                jobStatistics,
            },
            ui: { managerIsBusy },
        },
        actions: { setShowAllJobs },
    } = useDashboardContext();
    const device = useDeviceSize();
    const classes = useStyles({});

    const displayCount = (count: number | undefined | null) => {
        if (count === undefined) return "";
        if (count === null) return <Text variant="h2">n/a</Text>;
        return <Text variant="h2">{count}</Text>;
    };

    const displayStat = (
        value: number | undefined | null,
        formattedString: string
    ) => {
        if (value === undefined || value === null)
            return <Text variant="h2">n/a</Text>;
        return <Text variant="h2">{formattedString}</Text>;
    };

    const gotoSubmittedCandidates = React.useCallback(() => {
        goToCandidateSearchStatus({
            jobCandidateStatus: JobCandidateStatus.Submitted,
            showAllJobs,
            jobStatus: [JobStatus.Open, JobStatus.Paused],
        });
    }, [showAllJobs]);

    const statisticsData: PiePieceData[] = React.useMemo(
        () => [
            {
                id: 1,
                label: "open/paused without candidates",
                value: jobStatistics.notClosedWithEmptyPipelineCount,
                color: baseColors.navy,
                onSearch: () =>
                    goToJobSearch(
                        [JobStatus.Open, JobStatus.Paused],
                        showAllJobs,
                        false,
                        undefined
                    ),
            },
            {
                id: 2,
                label: "open/paused with candidates",
                value: jobStatistics.notClosedWithFilledPipelineCount,
                color: baseColors.magenta,
                onSearch: () =>
                    goToJobSearch(
                        [JobStatus.Open, JobStatus.Paused],
                        showAllJobs,
                        true,
                        undefined
                    ),
            },
            {
                id: 3,
                label: "closed with hires",
                value: jobStatistics.closedAndFilledCount,
                color: baseColors.green,
                onSearch: () =>
                    goToJobSearch(
                        [JobStatus.Closed],
                        showAllJobs,
                        undefined,
                        true
                    ),
            },
            {
                id: 4,
                label: "closed without hires",
                value: jobStatistics.closedAndNotFilledCount,
                color: baseColors.danger_red,
                onSearch: () =>
                    goToJobSearch(
                        [JobStatus.Closed],
                        showAllJobs,
                        undefined,
                        false
                    ),
            },
        ],
        [jobStatistics, showAllJobs]
    );

    const gotoInterviewingPastCandidates = React.useCallback(() => {
        goToCandidateSearchStatus({
            jobCandidateStatus: JobCandidateStatus.Interviewing,
            isInterviewingUpcoming: false,
            showAllJobs,
            jobStatus: [JobStatus.Open, JobStatus.Paused],
        });
    }, [showAllJobs]);

    const gotoInterviewingUpcomingCandidates = React.useCallback(() => {
        goToCandidateSearchStatus({
            jobCandidateStatus: JobCandidateStatus.Interviewing,
            isInterviewingUpcoming: true,
            showAllJobs,
            jobStatus: [JobStatus.Open, JobStatus.Paused],
        });
    }, [showAllJobs]);

    const gotoOfferedCandidates = React.useCallback(() => {
        goToCandidateSearchStatus({
            jobCandidateStatus: JobCandidateStatus.Offered,
            showAllJobs,
            jobStatus: [JobStatus.Open, JobStatus.Paused],
        });
    }, [showAllJobs]);

    const gotoHiredUnconfirmedCandidates = React.useCallback(() => {
        goToCandidateSearchStatus({
            jobCandidateStatus: JobCandidateStatus.Hired,
            isHiredConfirmed: false,
            showAllJobs,
        });
    }, [showAllJobs]);

    const gotoHiredConfirmedCandidates = React.useCallback(() => {
        goToCandidateSearchStatus({
            jobCandidateStatus: JobCandidateStatus.Hired,
            isHiredConfirmed: true,
            showAllJobs,
        });
    }, [showAllJobs]);

    const displayInterviewingPastSubTitle = (awaitingFeedback: number) => {
        if (!!awaitingFeedback)
            return (
                <>
                    <Text variant="h5" isInline={true}>
                        {awaitingFeedback}
                    </Text>{" "}
                    awaiting feedback
                </>
            );

        return null;
    };

    const handlePieClick = (data: PiePieceData) => {
        const { onSearch } = data;

        if (!!onSearch) onSearch();
    };

    const user = getUserFromStorage();

    const CandidatesTitle = (
        <div className={classes.sectionTitleFlex}>
            <Text dataCy="dashboardCandidatesTitle" variant="h2">{`${
                showAllJobs ? "all" : "my"
            } candidates`}</Text>
            {user.role === UserRole.ClientAdmin ? (
                <Switch
                    id="allJobsSwitch"
                    dataCy="dashboardToggle"
                    label={`${showAllJobs ? "all" : "my"} jobs`}
                    checked={showAllJobs}
                    onSwitchChange={() => setShowAllJobs(!showAllJobs)}
                />
            ) : null}
        </div>
    );

    const pieChartJobCount = statisticsData.reduce((a, b) => a + b.value, 0);

    return (
        <DashboardContent>
            <DashboardTileSection usePaper title={CandidatesTitle}>
                <DashboardTile
                    id="submittedTile"
                    data-cy="submittedTile"
                    title="submitted"
                    isBusy={managerIsBusy.submitted}
                    onClick={gotoSubmittedCandidates}
                    showAsLink={true}
                    small
                    useSubTitle
                >
                    {displayCount(managerStats.submitted)}
                </DashboardTile>
                <DashboardTile
                    id="interviewingPastTile"
                    data-cy="interviewingPastTile"
                    title="interviewed"
                    subTitle={displayInterviewingPastSubTitle(
                        managerStats.interviewingAwaitingFeedback
                    )}
                    isBusy={
                        managerIsBusy.interviewingPast ||
                        managerIsBusy.interviewingAwaitingFeedback
                    }
                    onClick={gotoInterviewingPastCandidates}
                    showAsLink={true}
                    small
                    useSubTitle
                >
                    {displayCount(managerStats.interviewingPast)}
                </DashboardTile>
                <DashboardTile
                    id="interviewingUpcomingTile"
                    data-cy="interviewingUpcomingTile"
                    title="interviewing"
                    isBusy={managerIsBusy.interviewingUpcoming}
                    onClick={gotoInterviewingUpcomingCandidates}
                    showAsLink={true}
                    small
                    useSubTitle
                >
                    {displayCount(managerStats.interviewingUpcoming)}
                </DashboardTile>
                <DashboardTile
                    id="offeredTile"
                    data-cy="offeredTile"
                    title="offered"
                    isBusy={managerIsBusy.offered}
                    onClick={gotoOfferedCandidates}
                    showAsLink={true}
                    small
                    useSubTitle
                >
                    {displayCount(managerStats.offered)}
                </DashboardTile>
                <DashboardTile
                    id="hiredAwaitingTile"
                    data-cy="hiredAwaitingTile"
                    title="hired"
                    subTitle="awaiting confirmation"
                    isBusy={managerIsBusy.hiredUnconfirmed}
                    onClick={gotoHiredUnconfirmedCandidates}
                    showAsLink={true}
                    small
                    useSubTitle
                >
                    {displayCount(managerStats.hiredUnconfirmed)}
                </DashboardTile>
                <DashboardTile
                    id="hiredConfirmedTile"
                    data-cy="hiredConfirmedTile"
                    title="hired"
                    subTitle="confirmed"
                    isBusy={managerIsBusy.hiredConfirmed}
                    onClick={gotoHiredConfirmedCandidates}
                    showAsLink={true}
                    small
                    useSubTitle
                >
                    {displayCount(managerStats.hiredConfirmed)}
                </DashboardTile>
            </DashboardTileSection>
            <DashboardTileSection
                usePaper
                data-cy="dashboardJobsTitle"
                title={
                    <Text dataCy="dashboardJobsTitle" variant="h2">
                        {showAllJobs ? "all" : "my"} jobs
                    </Text>
                }
            >
                <DashboardTile
                    title="pending"
                    id="pendingJobs"
                    isBusy={managerIsBusy.jobCounts}
                    onClick={() =>
                        goToJobSearchStatus(JobStatus.Pending, showAllJobs)
                    }
                    showAsLink={true}
                    small
                >
                    {displayCount(managerJobCounts.pending)}
                </DashboardTile>
                <DashboardTile
                    title="requested"
                    id="requestedJobs"
                    isBusy={managerIsBusy.jobCounts}
                    onClick={() =>
                        goToJobSearchStatus(JobStatus.Requested, showAllJobs)
                    }
                    showAsLink={true}
                    small
                >
                    {displayCount(managerJobCounts.requested)}
                </DashboardTile>
                <DashboardTile
                    title="open"
                    id="openJobs"
                    isBusy={managerIsBusy.jobCounts}
                    onClick={() =>
                        goToJobSearchStatus(JobStatus.Open, showAllJobs)
                    }
                    showAsLink={true}
                    small
                >
                    {displayCount(managerJobCounts.open)}
                </DashboardTile>
                <DashboardTile
                    title="paused"
                    id="pausedJobs"
                    isBusy={managerIsBusy.jobCounts}
                    onClick={() =>
                        goToJobSearchStatus(JobStatus.Paused, showAllJobs)
                    }
                    showAsLink={true}
                    small
                >
                    {displayCount(managerJobCounts.paused)}
                </DashboardTile>
                <DashboardTile
                    title="closed"
                    id="closedJobs"
                    isBusy={managerIsBusy.jobCounts}
                    onClick={() =>
                        goToJobSearchStatus(JobStatus.Closed, showAllJobs)
                    }
                    showAsLink={true}
                    small
                >
                    {displayCount(managerJobCounts.closed)}
                </DashboardTile>
                <Breakpoint mobile>
                    <DashboardTile id="emptyJobs" small>
                        <></>
                    </DashboardTile>
                </Breakpoint>
            </DashboardTileSection>
            <DashboardTileSection
                noPadding
                usePaper
                title={
                    <div className={classes.sectionTitleFlex}>
                        <Text dataCy="dashboardJobsOverviewTitle" variant="h2">
                            {showAllJobs ? "all" : "my"} jobs overview
                        </Text>
                        <Text variant="body2" className={classes.statsSyncDate}>
                            data updated nightly
                        </Text>
                    </div>
                }
            >
                <div className={classes.statisticsContainer}>
                    {managerIsBusy.stats ? (
                        <div className={classes.chartContainer}>
                            <DotLoader />
                        </div>
                    ) : (
                        <PieChart
                            className={classes.chartContainer}
                            data={statisticsData}
                            diameter={220}
                            legendWidth={275}
                            thickness={20}
                            animate={device === "desktop"}
                            offset={0.35}
                            onClick={handlePieClick}
                            centerElement={
                                <div className={classes.chartCenterContainer}>
                                    {pieChartJobCount >= 0 ? (
                                        <ButtonBase
                                            onClick={() =>
                                                goToJobSearch(
                                                    [
                                                        JobStatus.Open,
                                                        JobStatus.Paused,
                                                        JobStatus.Closed,
                                                    ],
                                                    showAllJobs
                                                )
                                            }
                                            className={classes.totalJobsButton}
                                        >
                                            <Text variant="h2">
                                                {pieChartJobCount}
                                            </Text>
                                            <Text
                                                className={
                                                    classes.chartJobTitle
                                                }
                                            >
                                                total jobs
                                            </Text>
                                        </ButtonBase>
                                    ) : null}
                                </div>
                            }
                        />
                    )}
                    <div className={classes.statsContainer}>
                        <div className={classes.statsContainer2}>
                            <span className={classes.statSectionTitle}>
                                success factors
                            </span>
                            <div className={classes.statsContainer3}>
                                <DashboardStatTile
                                    id="avgTimeToHire"
                                    title="time to hire"
                                    content={displayStat(
                                        jobStatistics.avgFirstHireTimeInDays,
                                        `${jobStatistics.avgFirstHireTimeInDays} days`
                                    )}
                                    isBusy={managerIsBusy.stats}
                                />
                                <DashboardStatTile
                                    id="avgTimeToSub"
                                    title="time to submit"
                                    content={displayStat(
                                        jobStatistics.avgFirstSubmitTimeInDays,
                                        `${jobStatistics.avgFirstSubmitTimeInDays} days`
                                    )}
                                    isBusy={managerIsBusy.stats}
                                />
                                <DashboardStatTile
                                    id="inSub"
                                    title="in submitted"
                                    content={displayStat(
                                        jobStatistics.avgTimeInSubmittedInDays,
                                        `${jobStatistics.avgTimeInSubmittedInDays} days`
                                    )}
                                    isBusy={managerIsBusy.stats}
                                />
                            </div>
                        </div>
                        <div
                            className={classnames(classes.statsContainer2, {
                                [classes.topBorder]: device === "desktop",
                            })}
                        >
                            <span className={classes.statSectionTitle}>
                                candidate quality
                            </span>
                            <div className={classes.statsContainer3}>
                                <DashboardStatTile
                                    id="subToInterview"
                                    title="submitted to interviewing"
                                    content={displayStat(
                                        jobStatistics.submittedToInterviewingPercentage,
                                        `${jobStatistics.submittedToInterviewingPercentage}%`
                                    )}
                                    isBusy={managerIsBusy.stats}
                                />
                                <DashboardStatTile
                                    id="interviewToHire"
                                    title="interviewing to hired"
                                    content={displayStat(
                                        jobStatistics.interviewingToHiredPercentage,
                                        `${jobStatistics.interviewingToHiredPercentage}%`
                                    )}
                                    isBusy={managerIsBusy.stats}
                                />
                                <DashboardStatTile
                                    id="attritionRate"
                                    title="attrition rate"
                                    content={displayStat(
                                        jobStatistics.hiredToRejectedPercentage,
                                        `${jobStatistics.hiredToRejectedPercentage}%`
                                    )}
                                    isBusy={managerIsBusy.stats}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </DashboardTileSection>
            {jobManagerDetails ? (
                <DashboardTileSection usePaper title="questions?" fitContent>
                    <QuestionsSection
                        id="dashboardQuestions"
                        jobManagerProfile={jobManagerDetails}
                    />
                </DashboardTileSection>
            ) : null}
        </DashboardContent>
    );
};
